import React, { useEffect, useRef, useState } from "react";
import type { FocusEvent } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import U2000Label from "../label/U2000Label";
import { format } from "date-fns";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import { getNestedValue, setNestedValue } from "../../../tools/U2000ObjectUtils";
import { parseDateWithFormat, validateDate } from "../../../tools/U2000DateUtils";
import U2000CalendarPopup, { type CalendarLabels } from "../calendar/U2000CalendarPopup";
import { useTranslation } from "react-i18next";

interface U2000InputTagDateProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  readonly?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  formatter?: ((date: Date) => string) | null;
  placeholder?: string;
}

const U2000InputTagDate: React.FC<U2000InputTagDateProps> = ({ formStateHandlers, fieldName, label, readonly = false, onFocus, onBlur }) => {
  const tags: string[] = getNestedValue(formStateHandlers.getData(), fieldName) || [];
  const [inputValue, setInputValue] = useState<string>("");
  const [onInput, setOnInput] = React.useState(false);
  const inputTagRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const [isOpenCalendar, setIsOpenCalendar] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const dateFormat = "yyyy-MM-dd";
  const { t } = useTranslation();

  // Préparer les labels pour le calendrier popup
  const calendarLabels: CalendarLabels = {
    monthNames: [
      t("lblCalendarJan"),
      t("lblCalendarFeb"),
      t("lblCalendarMar"),
      t("lblCalendarApr"),
      t("lblCalendarMay"),
      t("lblCalendarJun"),
      t("lblCalendarJul"),
      t("lblCalendarAug"),
      t("lblCalendarSep"),
      t("lblCalendarOct"),
      t("lblCalendarNov"),
      t("lblCalendarDec"),
    ],
    dayNames: [t("lblCalendarSun"), t("lblCalendarMon"), t("lblCalendarTue"), t("lblCalendarWed"), t("lblCalendarThu"), t("lblCalendarFri"), t("lblCalendarSat")],
    today: t("lblCalendarToday"),
    clear: t("lblCalendarClear"),
  };

  /**
   * Valide si une chaîne correspond à une date valide selon le format.
   * @param {string} dateStr - Date en string
   * @returns {boolean}
   */
  function validateDateStr(dateStr: string): boolean {
    return validateDate(dateStr, dateFormat);
  }

  /**
   * Ajoute la date sélectionnée comme tag.
   * @param {Date} selectedDate - Date JS sélectionnée
   */
  function handleSelectData(selectedDate: Date) {
    const cleaned = format(selectedDate, dateFormat);
    const newTags = [...tags, cleaned];
    const currentValue = formStateHandlers.getData();
    setNestedValue(currentValue, fieldName, newTags);
    formStateHandlers.setData({ ...currentValue });
    setInputValue("");
    // Ne pas fermer le calendrier en mode multi pour permettre plusieurs sélections
  }

  function handleTodayClick() {
    const cleaned = format(new Date(), dateFormat);
    if (!tags.includes(cleaned)) {
      const newTags = [...tags, cleaned];
      const currentValue = formStateHandlers.getData();
      setNestedValue(currentValue, fieldName, newTags);
      formStateHandlers.setData({ ...currentValue });
    }
    setCurrentMonth(new Date());
    setCurrentDate(new Date());
  }

  function handleClearClick() {
    setInputValue("");
    const currentValue = formStateHandlers.getData();
    setNestedValue(currentValue, fieldName, []);
    formStateHandlers.setData({ ...currentValue });
    setIsOpenCalendar(false);
  }

  /**
   * Gère la saisie manuelle d'une date dans l'input.
   * @param {string} val - Valeur saisie
   */
  function hangleInputChangeDate(val: string) {
    let v = val.replace(/[^0-9/\-]/g, "");
    if (validateDateStr(v)) {
      const date = parseDateWithFormat(v, dateFormat);
      if (date) {
        handleSelectData(date);
        setCurrentMonth(new Date());
        setCurrentDate(new Date());
        v = "";
      }
    }
    setInputValue(v);
  }

  /**
   * Gère les touches clavier sur l'input date :
   * - Entrée ou virgule : ajoute la date
   * - Backspace à vide : supprime le dernier tag
   * - Escape : ferme le calendrier
   * - F8 : ouvre le calendrier
   * @param {React.KeyboardEvent} e
   */
  function handleInputDateKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim() !== "") {
      hangleInputChangeDate(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      const newTags = tags.slice(0, -1);
      const currentValue = formStateHandlers.getData();
      setNestedValue(currentValue, fieldName, newTags);
      formStateHandlers.setData({ ...currentValue });
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpenCalendar(false);
    } else if (e.key === "F8") {
      e.preventDefault();
      if (readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ) return;
      setIsOpenCalendar(true);
    }
  }

  /**
   * Supprime le tag à l'index donné.
   * @param {number} index - Index du tag à supprimer
   */
  function handleRemoveTag(index: number) {
    const newTags = tags.filter((_tag: string, i: number) => i !== index);
    const currentValue = formStateHandlers.getData();
    setNestedValue(currentValue, fieldName, newTags);
    formStateHandlers.setData({ ...currentValue });
  }

  /**
   * Ouvre ou ferme le calendrier (popup).
   */
  function handleCalendarClick() {
    if (readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ) return;
    setIsOpenCalendar(!isOpenCalendar);
  }

  /**
   * Gère le blur du calendrier (fermeture si focus sort).
   * @param {React.FocusEvent} e
   */
  function onBlurCalendar(e: React.FocusEvent<HTMLInputElement>) {
    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || (inputTagRef.current && !inputTagRef.current.contains(relatedTarget))) {
      let v = inputValue.replace(/[^0-9/\-]/g, "");
      if (!validateDateStr(v)) {
        setInputValue("");
      }
      setIsOpenCalendar(false);
      setOnInput(false);
      if (onBlur) onBlur(e);
      if (formStateHandlers.onBlur) formStateHandlers.onBlur(e);
    }
  }

  useEffect(() => {
    if (!isOpenCalendar) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (parentRef.current && !parentRef.current.contains(target) && inputTagRef.current && !inputTagRef.current.contains(target)) {
        setIsOpenCalendar(false);
        setOnInput(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpenCalendar]);

  const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const hasError = formStateHandlers.getErrors()[fieldName];

  return (
    <div className="space-y-1.5">
      <U2000Label label={label} />
      <div
        className={`
          w-full h-[46px] rounded-lg border px-4 py-1
          transition-all duration-200 ease-in-out
          ${
            isDisabled
              ? "border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed"
              : hasError
              ? "border-red-300 bg-red-50 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100"
              : onInput
              ? "border-blue-500 bg-white ring-2 ring-blue-100"
              : "border-neutral-300 bg-white hover:border-neutral-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100"
          }
        `}
        tabIndex={-1}
        onFocus={() => !isDisabled && setOnInput(true)}
        ref={inputTagRef}
      >
        <div className="flex flex-wrap items-center gap-2 h-full min-h-0">
          {tags.map((tag: string, idx: number) => (
            <span
              key={idx}
              className="
                inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full max-w-xs
                bg-blue-50 text-blue-700 border border-blue-200
                transition-all duration-150 hover:bg-blue-100
              "
            >
              {tag}
              {!isDisabled && (
                <button
                  type="button"
                  className="
                    ml-1 flex h-3 w-3 items-center justify-center rounded-full text-blue-600
                    transition-all duration-150 hover:bg-blue-200 hover:text-blue-800
                    focus:outline-hidden
                  "
                  onClick={() => handleRemoveTag(idx)}
                  tabIndex={-1}
                  disabled={isDisabled}
                  aria-label={`Supprimer ${tag}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {!readonly && (
            <div className="relative flex min-w-0 flex-1 items-center" ref={parentRef}>
              <div className="flex w-full items-center" onBlur={onBlurCalendar}>
                <input
                  type="text"
                  value={inputValue}
                  placeholder={dateFormat}
                  className="
                    min-w-0 flex-1 bg-transparent text-sm outline-hidden
                    placeholder-neutral-400 transition-colors duration-200
                    focus:placeholder-neutral-300
                  "
                  onChange={(e) => hangleInputChangeDate(e.target.value)}
                  onFocus={onFocus}
                  disabled={isDisabled}
                  onKeyDown={handleInputDateKeyDown}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={handleCalendarClick}
                  className="
                    ml-2 flex h-8 w-8 items-center justify-center rounded-md
                    text-gray-400 transition-colors duration-200
                    hover:bg-gray-50 hover:text-gray-600
                    focus:outline-hidden focus:ring-2 focus:ring-primary-500
                  "
                  disabled={isDisabled}
                  aria-label="Ouvrir le calendrier"
                >
                  <FaCalendarAlt className="w-4 h-4" />
                </button>
              </div>
              <U2000CalendarPopup
                isOpen={isOpenCalendar}
                currentDate={currentDate}
                currentMonth={currentMonth}
                mode="multi"
                position="bottom"
                selectedValues={tags}
                labels={calendarLabels}
                onSelectDate={handleSelectData}
                onTodayClick={handleTodayClick}
                onClearClick={handleClearClick}
                onMonthChange={setCurrentMonth}
                onClose={() => setIsOpenCalendar(false)}
                zIndex={1000}
              />
            </div>
          )}
        </div>
      </div>
      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputTagDate;
